// CrewDrawer — right slide-in master-detail for one crew member's utilization.
//
// Built on the shared ui/sheet (Radix) primitive — the house slide-in panel
// (right edge, overlay + focus-trap + Escape-to-close + built-in top-right X).
// Shows the person's name + CREDITED period totals, then a table of their
// distinct jobs over the range (Job — Customer | Travel | LF/SF | dates).
// House tokens only — no UtilityDesk imports. Capture/KPI only, no $.
//
// Data is lazy-fetched on open via getCrewPersonDetail (month range). The open
// person is reflected in the URL (?crew=<id>) by the parent so refresh restores.

import { useQuery } from '@tanstack/react-query';
import { Clock } from 'lucide-react';
import type { CrewPersonJobRow } from '@gutterinv/shared';
import { getCrewPersonDetail } from '../api/client.js';
import { qk } from '../lib/queryKeys.js';
import { formatHours, footageUnitLabel } from '../lib/time-entry-format.js';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from './ui/sheet.js';

/** Compact footage label dropping a trailing .0 ("180" not "180.0"). */
function formatFootage(n: number): string {
  return String(Number(n.toFixed(2)));
}

interface CrewDrawerProps {
  crewMemberId: string | null;
  /** Fallback name (from the table row) shown while the detail loads. */
  fallbackName?: string;
  from: string;
  to: string;
  /** Human label for the range, e.g. "This month". */
  rangeLabel: string;
  onClose: () => void;
}

export function CrewDrawer({
  crewMemberId,
  fallbackName,
  from,
  to,
  rangeLabel,
  onClose,
}: CrewDrawerProps) {
  const open = crewMemberId !== null;

  const { data, isPending, error } = useQuery({
    queryKey: qk.crewUtilization.person(crewMemberId ?? '', from, to),
    queryFn: () => getCrewPersonDetail(crewMemberId!, from, to),
    enabled: open,
  });

  const name = data?.crewMemberName ?? fallbackName ?? 'Crew member';

  return (
    <Sheet open={open} onOpenChange={(next) => { if (!next) onClose(); }}>
      <SheetContent
        aria-label={`${name} utilization`}
        className="w-[480px] max-w-[100vw] gap-0 p-0 sm:max-w-[480px]"
      >
        {/* Header */}
        <SheetHeader className="space-y-0 px-4 py-4">
          <div className="min-w-0 pr-8">
            <div className="eyebrow">Crew member · {rangeLabel}</div>
            <SheetTitle className="text-lg font-display font-semibold text-foreground leading-tight truncate mt-0.5">
              {name}
            </SheetTitle>
          </div>

          {/* Credited period totals */}
          {data && (
            <div className="grid grid-cols-2 gap-2 pt-4 sm:grid-cols-4">
              <DrawerStat label="Travel" value={formatHours(data.totals.travelHours)} />
              <DrawerStat label="Jobs" value={String(data.totals.jobCount)} />
              <DrawerStat label="LF credited" value={formatFootage(data.totals.lfFootage)} />
              <DrawerStat label="SF credited" value={formatFootage(data.totals.sfFootage)} />
            </div>
          )}
        </SheetHeader>

        {/* Body */}
        <SheetBody className="p-4">
          {isPending && (
            <div className="text-sm text-muted-foreground">Loading…</div>
          )}
          {error && (
            <div className="text-sm text-destructive">
              Failed to load: {error instanceof Error ? error.message : 'unknown error'}
            </div>
          )}
          {data && data.jobs.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 py-10">
              <Clock className="h-6 w-6 text-taupe" />
              <span className="text-sm text-muted-foreground">
                No jobs worked in this range
              </span>
            </div>
          )}
          {data && data.jobs.length > 0 && (
            <div className="rounded-lg border border-border bg-card overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-secondary/50 text-xs uppercase tracking-wide text-muted-foreground">
                    <th className="text-left px-3 py-2 font-semibold">Job</th>
                    <th className="text-right px-3 py-2 font-semibold w-16">Travel</th>
                    <th className="text-right px-3 py-2 font-semibold w-24">Footage</th>
                  </tr>
                </thead>
                <tbody>
                  {data.jobs.map((j) => (
                    <JobRow key={j.jobId} job={j} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

function DrawerStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-secondary/30 px-3 py-2">
      <div className="eyebrow text-[9px]">{label}</div>
      <div className="text-base font-bold tnum mt-0.5 text-foreground">{value}</div>
    </div>
  );
}

function JobRow({ job }: { job: CrewPersonJobRow }) {
  const dates =
    job.firstDate === job.lastDate
      ? job.firstDate
      : `${job.firstDate} – ${job.lastDate}`;
  return (
    <tr className="border-t border-border/40">
      <td className="px-3 py-2.5">
        <span className="font-medium">{job.displayName}</span>
        <span className="text-muted-foreground"> — {job.customerName}</span>
        <div className="text-xs text-muted-foreground tnum mt-0.5">{dates}</div>
      </td>
      <td className="tnum text-right px-3 py-2.5 align-top">{formatHours(job.travelHours)}</td>
      <td className="tnum text-right px-3 py-2.5 align-top">
        {formatFootage(job.footage)}{' '}
        <span className="text-xs text-muted-foreground">{footageUnitLabel(job.productLine)}</span>
      </td>
    </tr>
  );
}